/**
 * Processing Status Value Object
 * Represents the count of items in each processing status
 */
export interface ProcessingStatusBreakdown {
    [key: string]: number;
}

/**
 * Pasco Stats Value Object
 * Represents PASCO-related statistics
 */
export interface PascoStats {
    totalPascos: number;
    totalQuestionChunks: number;
    statusBreakdown: ProcessingStatusBreakdown;
    pendingApproval: number;
    approved: number;
}

/**
 * Dashboard Stats Value Object
 * Represents dashboard-level statistics
 */
export interface DashboardStats {
    totalUsers: number;
    activeUsers: number;
    bannedUsers: number;
    verifiedUsers: number;
    usersWithRoles: number;
    usersWithoutRoles: number;
    totalRoles: number;
    activeRoles: number;
    totalPermissions: number;
}

/**
 * Pasco Job Stats Value Object
 * Represents PASCO job-related statistics
 */
export interface PascoJobStats {
    totalJobs: number;
    statusBreakdown: ProcessingStatusBreakdown;
}

/**
 * Stats Value Object
 * Domain representation of application statistics
 * 
 * This is a value object that encapsulates statistics data
 * following DDD principles. It should be immutable and contain
 * only domain-level concerns.
 */
export class Stats {
    private constructor(
        private readonly _pasco: PascoStats,
        private readonly _dashboard: DashboardStats,
        private readonly _pascoJobs: PascoJobStats,
        private readonly _timestamp: Date
    ) {
        this.validate();
    }

    /**
     * Create a new Stats instance
     */
    static create(props: {
        pasco: PascoStats;
        dashboard: DashboardStats;
        pascoJobs: PascoJobStats;
        timestamp: Date;
    }): Stats {
        return new Stats(
            props.pasco,
            props.dashboard,
            props.pascoJobs,
            props.timestamp
        );
    }

    /**
     * Validate the stats data
     */
    private validate(): void {
        // Validate PASCO stats
        if (this._pasco.totalPascos < 0) {
            throw new Error('Total PASCOs cannot be negative');
        }

        if (this._pasco.totalQuestionChunks < 0) {
            throw new Error('Total question chunks cannot be negative');
        }

        // Validate dashboard stats
        if (this._dashboard.totalUsers < 0) {
            throw new Error('Total users cannot be negative');
        }

        if (this._dashboard.activeUsers < 0) {
            throw new Error('Active users cannot be negative');
        }

        if (this._dashboard.totalRoles < 0) {
            throw new Error('Total roles cannot be negative');
        }
    }

    // Getters
    get pasco(): PascoStats {
        return {
            ...this._pasco,
            statusBreakdown: { ...this._pasco.statusBreakdown }
        };
    }

    get dashboard(): DashboardStats {
        return { ...this._dashboard };
    }

    get pascoJobs(): PascoJobStats {
        return {
            ...this._pascoJobs,
            statusBreakdown: { ...this._pascoJobs.statusBreakdown }
        };
    }

    get timestamp(): Date {
        return this._timestamp;
    }

    /**
     * Get PASCO completion percentage
     */
    getPascoCompletionPercentage(): number {
        const completed = this._pasco.statusBreakdown['completed'] || 0;
        if (this._pasco.totalPascos === 0) {
            return 0;
        }
        return (completed / this._pasco.totalPascos) * 100;
    }

    /**
     * Get formatted PASCO completion percentage (e.g., "85.5")
     */
    getFormattedPascoCompletionRate(): string {
        return this.getPascoCompletionPercentage().toFixed(1);
    }

    /**
     * Get total number of PASCO tasks (sum of all statuses)
     */
    getTotalPascoTasks(): number {
        return Object.values(this._pasco.statusBreakdown).reduce((sum, count) => sum + count, 0);
    }

    /**
     * Get user verification percentage
     */
    getUserVerificationPercentage(): number {
        if (this._dashboard.totalUsers === 0) {
            return 0;
        }
        return (this._dashboard.verifiedUsers / this._dashboard.totalUsers) * 100;
    }

    /**
     * Get user activity percentage
     */
    getUserActivityPercentage(): number {
        if (this._dashboard.totalUsers === 0) {
            return 0;
        }
        return (this._dashboard.activeUsers / this._dashboard.totalUsers) * 100;
    }

    /**
     * Check if stats are recent (within last 5 minutes)
     */
    isRecent(thresholdMinutes: number = 5): boolean {
        const now = new Date();
        const diffMs = now.getTime() - this._timestamp.getTime();
        const diffMinutes = diffMs / 1000 / 60;
        return diffMinutes <= thresholdMinutes;
    }

    /**
     * Check if any PASCOs have failed
     */
    hasPascoFailures(): boolean {
        return (this._pasco.statusBreakdown['failed'] || 0) > 0;
    }

    /**
     * Check if all PASCOs are completed
     */
    isAllPascosCompleted(): boolean {
        const completed = this._pasco.statusBreakdown['completed'] || 0;
        return completed === this._pasco.totalPascos && this._pasco.totalPascos > 0;
    }
}
